// External
import React, { Component } from "react";
import { connect } from "react-redux";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Moment from "moment";
import { cloneDeep, debounce, get as _get, isNull, startCase } from "lodash";

// Internal
import DraggableTurfAssignment from "../cards/DraggableTurfAssignment";
import DraggableShiftCanvasser from "../cards/DraggableShiftCanvasser";
import { DoorCrewCard, parseCreateSeatDropTarget } from "../doors";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FilterDisplay from "../subscreens/FilterDisplay";
import NoteTagFilter from "../theme/forms/NoteTagFilter";
import NoteTagList from "../theme/buttons/NoteTag";
import {
  filterAndSortItems,
  filterItemsByNoteTag,
  handleSearchChange,
  sortItems,
} from "../../utils/FilterDisplayUtils";
import CanvasserLegend from "../theme/modals/CanvasserLegend";
import {
  CANVASSER_STATUS_COLORS,
  findCanvassersAssignedForSelection,
  findCheckedInCanvassers,
} from "../../utils/schedulerUtils";

const ALL_SHIFTS = "all";

import api from "../../store/api/api";
import {
  createShift,
  createShiftCanvasser,
  getSchedule,
  pullCampaignCanvassers,
  pullCampaignDoorCanvassPasses,
  pullSiteNoteCategories,
  updateShiftCanvasser,
} from "../../store/api/calls";

import * as doorCalls from "../../store/api/calls/doors";
import { updateDoorCrewCanvasser } from "../../store/api/calls/doors";

import {
  setCrews,
  setCrewsShiftData,
  setSessionVar,
} from "../../store/actions";

import NavBaseTemplate from "../templates/NavBaseTemplate";
import FeedbackTile from "../theme/modals/FeedbackTile";
import Tabs from "../theme/menus/Tabs";
import Input from "../theme/forms/Input";
import BaseModal from "../theme/modals/BaseModal";
import FromToDatePickerInput from "../theme/forms/FromToDatePickerInput";
import TurfNoteModal from "../theme/modals/TurfNoteModal";
import { withRouter } from "../../utils/withRouter";

class DoorSchedule extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      pageError: false,
      errMsg: "There was a server error",
      turfs: [],
      crews: [],
      crewsDrop: [],
      displayedCanvassers: [],
      selectedDate: new Moment(Date.now()).format("YYYY-MM-DD"),
      sideBarSelection: "turfs",
      showLegend: false,
      showAssignedCanvassers: true,
      crewDropMatActive: false,
      //canvasser filters
      availableCanvasserStatuses: [
        "checked_in",
        "not_checked_in",
        "unscheduled",
      ],
      displayedCanvasserStatuses: [
        "checked_in",
        "not_checked_in",
        "unscheduled",
      ],
      //turf filters
      turfStatusFilters: ["walked"],
      turfPassFilter: 0,
      showTurfFilters: false,
      intendedWalkStartDate: null,
      intendedWalkEndDate: null,
      turfCodeFilter: "walkable",
      canvasserFilters: {
        id: "canvassers",
        keyword: "",
        keywordPersist: "",
        noteTags: [],
        sortBy: "canvasser_name",
        sortDsc: false,
        searchFields: ["firstname", "lastname", "created_at"],
        sortFields: [
          {
            display: "Name",
            value: "canvasser_name",
          },
          {
            display: "Creation Date",
            value: "created_at",
          },
          {
            display: "Status",
            value: "status",
          },
        ],
      },

      turfFilters: {
        id: "turfs",
        keyword: "",
        keywordPersist: "",
        noteTags: [],
        sortBy: "name",
        sortDsc: false,
        searchFields: [],
        sortFields: [
          {
            display: "Name",
            value: "name",
          },
          {
            display: "Doors",
            value: "household_count",
          },
          {
            display: "Pass",
            value: "doorcanvasspass",
          },
        ],
      },

      // shifts
      shiftData: [],
      showShiftModal: false,
      shiftStart: undefined,
      shiftEnd: undefined,
      invalidInput: false,
      invalidTime: false,
      shiftError: false,
      lockShiftSubmit: false,
      // crews
      crewsheet_modal_show: false,
      crewsheet_crews: [],
      crewsheet_get_dirs: true,
      crewsheet_selected_shift_idx: 0,

      focusedOffice: 0,
      focusedDoorCanvassName: "",
    };

    this.focusedDoorCanvass = 0;

    this.colorClassMapping = CANVASSER_STATUS_COLORS;

    this.turfClassMapping = {
      // checked_in: {class: '_green', color: "#33a02c"},
      walked: { class: "_blue", color: "#1f78b4" },
      assigned: { class: "_red", color: "#e31a1c" },
      unwalked: { class: "_green", color: "#33a02c" },
    };

    this.dateChangeTimeout = undefined;
  }

  isShowingAllShifts = () => this.state.crewsheet_selected_shift_idx === -1;

  getSelectedShiftId = () => {
    if (this.isShowingAllShifts()) return null;
    const shift = this.state.shiftData[this.state.crewsheet_selected_shift_idx];
    return shift?.id || null;
  };

  // Shift ids for the focused office+day (and selected shift, when not All).
  // Door crews for this canvass may also sit on other offices' shifts — those
  // must not affect the board, facet counts, or status colors.
  getVisibleShiftIds = () => {
    if (this.isShowingAllShifts()) {
      return new Set(this.state.shiftData.map((s) => +s.id));
    }
    const shiftId = this.getSelectedShiftId();
    return shiftId ? new Set([+shiftId]) : new Set();
  };

  getShiftLabel = (shift) => {
    if (!shift) return "";
    return (
      new Moment(`${shift.shift_date} ${shift.start_time}`).format("LT") +
      " - " +
      new Moment(`${shift.shift_date} ${shift.end_time}`).format("LT")
    );
  };

  getVisibleCrews = () => {
    const shiftIds = this.getVisibleShiftIds();
    return cloneDeep(this.state.crews).filter((crew) =>
      shiftIds.has(+crew.shift)
    );
  };

  getVisibleAssignedCanvasserIds = () => {
    // Unique ids — one person on two seats still counts once, matching status facets.
    return [
      ...new Set(
        findCanvassersAssignedForSelection(this.getVisibleCrews(), ALL_SHIFTS)
      ),
    ];
  };

  setCrewDropMatsActive = (active, { inline = false } = {}) => {
    const mats = document.querySelectorAll(".crew-drop-mat");
    mats.forEach((mat) => {
      let className = "crew-drop-mat";
      if (active) {
        className += " active";
        if (inline) className += " inline";
      } else if (inline) {
        className += " inline";
      }
      mat.className = className;
    });
  };

  componentDidMount() {
    pullSiteNoteCategories();
    if (this.props.focusedCampaign !== 0 && !this.props.focusedCampaign.id) {
      setTimeout(() => this.componentDidMount(), 200);
    } else {
      this.getDataOnLoad();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.dateChangeTimeout);
  }

  componentDidUpdate(prevProps) {
    if (
      !this.state.loading &&
      this.state.turfs.length &&
      prevProps.crews !== this.props.crews
    ) {
      this.setCrewsFromProps();
    }
  }

  /*
   * Loads all shifts for the office+day. Canvasser attendance is merged across
   * those shifts into one sidebar card; the board can show All shifts or one.
   */
  getAndParseSchedule = () => {
    this.setState({ loading: true });
    return getSchedule(this.state.focusedOffice, this.state.selectedDate)
      .then((data) => {
        let scs = [];
        let scsById = {};
        data.forEach((d) => {
          d.canvassers.forEach((shift_canvasser) => {
            let c = this.props.canvassers.find(
              (item) => item.id === shift_canvasser.canvasser
            );

            if (!c) return;

            const assigned =
              !isNull(shift_canvasser.crew) ||
              !isNull(shift_canvasser.doorcrew);

            if (!scsById[c.id]) {
              const entry = {
                ...c,
                shift: shift_canvasser.shift,
                present: !!shift_canvasser.present,
                assigned,
                scheduled: !!shift_canvasser.scheduled,
              };
              scsById[c.id] = entry;
              scs.push(entry);
            } else {
              // Merge across same-day shifts so All-shifts view has one card
              // with the strongest attendance / assignment signal.
              const entry = scsById[c.id];
              entry.scheduled = entry.scheduled || !!shift_canvasser.scheduled;
              entry.present =
                entry.present ||
                (!!shift_canvasser.scheduled && !!shift_canvasser.present);
              entry.assigned = entry.assigned || assigned;
            }
          });
        });

        let scheduled_ids = scs.length ? scs.map((_d) => _d.id) : [];
        // Strip stale schedule flags from Redux canvassers so a prior office's
        // attendance does not bleed into this office's sidebar.
        let final_canvassers = [
          ...scs,
          ...this.props.canvassers
            .filter((c) => !scheduled_ids.includes(c.id))
            .map((c) => ({
              ...c,
              shift: undefined,
              present: false,
              scheduled: false,
              assigned: false,
              status: "unscheduled",
            })),
        ];

        let nextShiftIdx = this.state.crewsheet_selected_shift_idx;
        // Keep All shifts (-1); otherwise fall back to the first real shift.
        if (nextShiftIdx !== -1 && !data[nextShiftIdx]) {
          nextShiftIdx = 0;
        }

        // Status depends on shiftData + crews; set shiftData first, then map.
        this.setState(
          {
            shiftData: data,
            crewsheet_selected_shift_idx: nextShiftIdx,
          },
          () => {
            final_canvassers = final_canvassers
              .filter((n) => +n.office === this.state.focusedOffice)
              .map(this.setShiftCanvasserStatus);

            this.setState({ displayedCanvassers: final_canvassers });
            if (data.length) {
              const focused =
                nextShiftIdx === -1 ? data[0] : data[nextShiftIdx] || data[0];
              this.props.setCrewsShiftData(focused);
            }
          }
        );
      })
      .finally(() => this.setState({ loading: false }));
  };

  setShiftCanvasserStatus = (d) => {
    // Door Schedule status is relative to crews on the focused office's shifts —
    // not office-wide ShiftCanvasser attendance from other canvasses, and not
    // door crews that belong to another office's shifts on the same day.
    const shiftIds = this.getVisibleShiftIds();
    const onVisibleCrew = this.state.crews.some(
      (crew) =>
        shiftIds.has(+crew.shift) &&
        (crew.seats || []).some((s) => +s.canvasser === +d.id)
    );

    d.assigned = onVisibleCrew;

    if (onVisibleCrew && d.present) {
      d.status = "checked_in";
    } else if (onVisibleCrew) {
      d.status = "not_checked_in";
    } else {
      d.status = "unscheduled";
    }
    return d;
  };

  getDataOnLoad() {
    this.setState({ loading: true });
    const { params } = this.props;

    this.focusedDoorCanvass = +params.doorcanvass_id || 0;
    let fullDoorCanvass = this.props.doorCanvasses.find(
      (d) => +d.id === this.focusedDoorCanvass
    );
    // Match Schedule.jsx: assign office synchronously so the promise gate below sees it.
    if (fullDoorCanvass) {
      this.state.focusedOffice = +fullDoorCanvass.office;
      this.setState({
        focusedOffice: +fullDoorCanvass.office,
        focusedDoorCanvassName: fullDoorCanvass.name || "",
      });
    }

    // Door canvass id comes from url params
    new Promise((resolve, reject) => {
      if (this.state.focusedOffice) {
        resolve();
      } else {
        return api
          .get(`/doorcanvasses/${this.focusedDoorCanvass}/`)
          .then((res) => {
            this.setState({
              focusedOffice: +res.data.office,
              focusedDoorCanvassName: res.data.name || "",
            });
            return resolve();
          })
          .catch((err) => {
            console.error(err);
            this.setState({
              pageError: true,
              errMsg: "Error loading Door Canvass Data",
              loading: false,
            });

            this.setState({ focusedOffice: 0, focusedDoorCanvassName: "" });
            return reject();
          });
      }
    })
      .then(() => {
        Promise.all([
          pullCampaignDoorCanvassPasses(),
          pullCampaignCanvassers({
            // office: this.state.focusedOffice,
            abilities: 1,
          }),
          doorCalls.pullDoorCrews({
            canvass: this.focusedDoorCanvass,
            date: this.state.selectedDate,
          }),
          api
            .get("/turfs/", { canvass: this.focusedDoorCanvass, notes: 1 })
            .then((res) => res.data),
          doorCalls.getTurfCodes(),
        ])
          .then((data) => {
            return this.getAndParseSchedule().then((res) => {
              // crews correspond to the 4th call in the promiseAll above.
              let turfs = (data[3] || [])
                .filter((turf) => {
                  // hide locked turfs, as they shouldn't be schedulable
                  return !(
                    this.props.doorCanvassPasses.find(
                      (p) => p.id === turf.doorcanvasspass
                    ) || {}
                  ).is_locked;
                })
                .sort((a, b) => a.name.localeCompare(b.name)) //sort by name
                .map((d) => ({
                  ...d,
                  passname: (
                    this.props.doorCanvassPasses.find(
                      (p) => p.id === d.doorcanvasspass
                    ) || {}
                  ).name,
                })); //get the name of the turf's pass
              let crews = [...this.props.crews]
                .sort((a, b) => (a.id < b.id ? 1 : -1))
                .map((crew) => this.addTurfsToSeats(crew, turfs));
              let crewsheet_crews = crews.map((d) => d.id);
              this.setState(
                {
                  turfs,
                  crews,
                  crewsheet_crews,
                  loading: false,
                },
                () => {
                  this.setState({
                    displayedCanvassers: this.refreshCanvasserStatuses(),
                  });
                }
              );
            });
          })
          // if we have a shift, then we need to make sure that it is properly set
          .then(() => {
            if (this.state.shiftData.length && this.isShowingAllShifts()) {
              this.props.setCrewsShiftData(this.state.shiftData[0]);
            }
          })
          .catch((err) => {
            // Promise.all catch
            console.error(err);
            this.setState({
              loading: false,
              pageError: true,
              errMsg: "Error retrieving crew data.",
            });
          });
      })
      .catch((err) => {
        console.error(err);
        this.setState({ loading: false, pageError: true });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  onDragStart = (start) => {
    const { source } = start;
    // All-shifts uses per-section mats — keep them compact (inline) even when empty.
    const inline =
      this.isShowingAllShifts() || this.getVisibleCrews().length > 0;

    if (source.droppableId === "turfs") {
      this.setCrewDropMatsActive(true, { inline });
      this.makeAddTurfDropVisible(true);
    } else if (source.droppableId === "displayedCanvassers") {
      this.makeAddCanvasserDropVisible(true);
    }
  };

  //THIS FUNCTION HANDLES THE DROPPING OF A SITEROUTE INTO SCHEDULE TO CREATE A CREW
  onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    this.setCrewDropMatsActive(false, { inline: true });
    this.makeAddCanvasserDropVisible(false);
    this.makeAddTurfDropVisible(false);
    // destination will be null when dropped outside of context or it ended up in same column and same index
    if (
      !destination || //we don't know where to put it
      (destination.droppableId === source.droppableId &&
        destination.index === source.index) || //it didn't go anywhere.
      this.state.shiftData.length === 0 //there are no shifts to assign it to
    ) {
      this.setState({ crewDropMatActive: false });
      return;
    }

    const drop_is_seat_assignment =
      destination.droppableId.includes("turf-assign") &&
      source.droppableId === "displayedCanvassers";

    const drop_is_turf_reassignment =
      destination.droppableId.includes("turf-assign") &&
      source.droppableId === "turfs";

    const drop_is_seat_creation =
      destination.droppableId.includes("create-seat");

    // now need to reorder.
    const start = this.state[source.droppableId];

    let endId;
    if (destination.droppableId === "crewsDrop" || drop_is_seat_assignment) {
      endId = "crews";
    } else {
      endId = destination.droppableId;
    }

    let end = this.state[endId];

    if (start === end) {
      const newColumnIds = Array.from(start);
      let spliced = newColumnIds.splice(source.index, 1);
      if (spliced.length > 0) {
        newColumnIds.splice(destination.index, 0, spliced[0]);
      }

      const newState = {
        ...this.state,
        crewDropMatActive: false,
        [destination.droppableId]: newColumnIds,
      };

      this.setState(newState);
      return;
    }

    //handle canvasser seat assignment
    if (drop_is_seat_assignment) {
      let target_canvasser_id = draggableId.split("_")[1];
      let target_crewcanvasser_id =
        destination.droppableId.match(/_seat_(\d+)/)[1];
      this._handleCanvasserSelect(target_crewcanvasser_id, target_canvasser_id);
      return;
    }

    if (drop_is_seat_creation) {
      const dropTarget = parseCreateSeatDropTarget(destination.droppableId);
      if (!dropTarget) {
        return;
      }

      const { crewId, turfId: dropZoneTurfId } = dropTarget;

      if (
        source.droppableId === "displayedCanvassers" &&
        destination.droppableId.startsWith("create-seat_")
      ) {
        const canvasser_id = Number(draggableId.split("_")[1]);
        const turf_id = dropZoneTurfId ?? this.getInheritedTurfIdForCrew(crewId);
        if (!turf_id) {
          this.setState({
            pageError: true,
            errMsg:
              "Add a turf to this crew before dropping a canvasser to create a new seat.",
          });
          return;
        }
        this.createDoorCrewCanvasser(crewId, turf_id, canvasser_id);
      } else if (
        source.droppableId === "turfs" &&
        destination.droppableId.startsWith("create-seat-turf_")
      ) {
        const turfMatch = draggableId.match(/_(\d+)$/);
        const turf_id = turfMatch ? Number(turfMatch[1]) : null;
        this.createDoorCrewCanvasser(crewId, turf_id);
      }
      return;
    }

    if (drop_is_turf_reassignment) {
      return;
    }

    // move from one list to another. This is used for moving turfs to scheduled crews.
    const newStart = Array.from(start);
    const newFinish = Array.from(end);

    let item = newStart.slice(source.index, source.index + 1);

    if (item.length > 0) {
      newFinish.splice(destination.index, 0, { ...item[0] });
    }

    const newState = {
      ...this.state,
      crewDropMatActive: false,
      [source.droppableId]: start,
      [endId]: newFinish,
    };

    //last possibility is that we need to create a crew
    if (
      source.droppableId === "turfs" &&
      this.state.sideBarSelection === "turfs" &&
      (destination.droppableId === "crewsDrop" ||
        destination.droppableId.startsWith("crewsDrop_"))
    ) {
      let shift_id = false;
      if (destination.droppableId.startsWith("crewsDrop_")) {
        shift_id = Number(destination.droppableId.replace("crewsDrop_", ""));
      } else {
        shift_id = this.getSelectedShiftId();
      }
      let turf_id = Number(draggableId.split("_")[1]);

      if (!shift_id) {
        this.setState({
          pageError: true,
          errMsg:
            "Select a specific shift (or drop onto a shift section) to create a crew.",
        });
        return;
      }

      // Need to do api call to preserve new crew and then call setState in then
      let requestData = {
        canvass: this.focusedDoorCanvass,
        shift: shift_id,
        seats: [{ turf: turf_id }],
      };

      this.setState({ loading: true });

      doorCalls
        .createDoorCrew(requestData)
        .then((crew) => {
          crew = this.addTurfsToSeats(crew);
          newState.loading = false;
          newState.turfs = newState.turfs.map((d) =>
            d.id === turf_id ? { ...d, assigned: 1 } : d
          );
          this.setState(newState);
          this.setCrewsFromProps();
          return crew;
        })
        .then((crew) => {
          // HACK: Seats weren't showing up because of a race condition
          // that probably exists in the api where crew_id pks aren't valid by the time
          // the serializer calls them
          !crew.seats.length && this.createDoorCrewCanvasser(crew.id, turf_id);
        });
    } else {
      this.setState(newState);
    }
  };

  addTurfsToSeats = (crew, turfs) => {
    if (!turfs) turfs = this.state.turfs;
    crew = cloneDeep(crew);
    crew.seats.forEach((seat) => {
      let turf_id = typeof seat.turf == "object" ? seat.turf.id : seat.turf; //on incoming this can be an id. on update, this is an object
      let turf = turfs.find((d) => d.id == turf_id);
      seat.turf = turf ?? (turf_id != null ? { _raw_id: turf_id } : undefined);
    });

    return crew;
  };

  getSeatTurfId = (seat) => {
    if (!seat?.turf) {
      return null;
    }
    if (typeof seat.turf === "object") {
      return seat.turf.id ?? seat.turf._raw_id ?? null;
    }
    return seat.turf;
  };

  /** When adding a seat via canvasser drop, reuse the crew's most recent turf. */
  getInheritedTurfIdForCrew = (crew_id) => {
    const crew = this.state.crews.find((d) => d.id === crew_id);
    if (!crew?.seats?.length) {
      return null;
    }
    for (let i = crew.seats.length - 1; i >= 0; i--) {
      const turfId = this.getSeatTurfId(crew.seats[i]);
      if (turfId) {
        return turfId;
      }
    }
    return null;
  };

  refreshCanvasserStatuses = (canvassers = this.state.displayedCanvassers) => {
    return canvassers.map((d) => this.setShiftCanvasserStatus({ ...d }));
  };

  setCrewsFromProps = () => {
    let crews = [...this.props.crews]
      .sort((a, b) => (a.id < b.id ? 1 : -1))
      .map((crew) => this.addTurfsToSeats(crew));

    this.setState({ crews }, () => {
      this.setState({
        displayedCanvassers: this.refreshCanvasserStatuses(),
      });
    });
  };

  makeAddCanvasserDropVisible = (show = true) => {
    const elements = document.getElementsByClassName("add-canvasser-drop-mat");
    for (let i = 0; i < elements.length; i++) {
      elements[i].className = show
        ? "add-canvasser-drop-mat add-seat-drop-mat visible active"
        : "add-canvasser-drop-mat add-seat-drop-mat visible";
    }
  };

  makeAddTurfDropVisible = (show = true) => {
    const elements = document.getElementsByClassName("add-turf-drop-mat");
    for (let i = 0; i < elements.length; i++) {
      elements[i].className = show
        ? "add-turf-drop-mat add-seat-drop-mat visible active"
        : "add-turf-drop-mat add-seat-drop-mat visible";
    }
  };

  makeAddSeatDropVisible = (show = true) => {
    this.makeAddCanvasserDropVisible(show);
    this.makeAddTurfDropVisible(show);
  };

  createDoorCrewCanvasser(crew_id, turf_id, canvasser_id) {
    return doorCalls
      .createDoorCrewCanvasser({
        crew: crew_id,
        turf: turf_id ?? undefined,
        canvasser: canvasser_id ?? undefined,
      })
      .then((updatedCrew) => {
        let crews;
        if (updatedCrew?.id) {
          crews = this.state.crews.map((d) =>
            d.id === updatedCrew.id
              ? this.addTurfsToSeats(cloneDeep(updatedCrew))
              : d
          );
        } else {
          crews = [...this.props.crews]
            .sort((a, b) => (a.id < b.id ? 1 : -1))
            .map((crew) => this.addTurfsToSeats(crew));
        }

        let turfs = this.state.turfs;
        if (turf_id) {
          turfs = turfs.map((d) =>
            d.id === turf_id ? { ...d, assigned: 1 } : d
          );
        }
        this.setState({ crews, turfs, pageError: false });

        if (canvasser_id) {
          this.updateOrCreateShiftCanvasser(canvasser_id, crew_id);
        }
      })
      .catch(() => {
        this.setState({
          pageError: true,
          errMsg: "Could not add seat. Make sure the crew has a turf assigned.",
        });
      });
  }

  updateDoorCrewCanvasser = (seat_id, seat_changes) => {
    return updateDoorCrewCanvasser(seat_id, seat_changes).then((data) => {
      // the full and updated crew is returned.
      this.setCrewsFromProps();
    });
  };

  updateOrCreateShiftCanvasser(canvasser_id, crew_id, shiftIdOverride = null) {
    // Prefer the crew's shift so All-shifts mode writes the correct ShiftCanvasser.
    let shift_id = shiftIdOverride;
    if (!shift_id && crew_id) {
      const crew = this.state.crews.find((c) => c.id === +crew_id);
      shift_id = crew?.shift || null;
    }
    if (!shift_id) {
      shift_id = this.getSelectedShiftId();
    }
    if (!shift_id) {
      const fromList = this.state.displayedCanvassers.find(
        (d) => d.id === +canvasser_id && d.shift
      );
      shift_id = fromList?.shift || this.state.shiftData[0]?.id;
    }
    if (!shift_id) return;

    const existsOnShift = this.state.shiftData.some(
      (shift) =>
        shift.id === +shift_id &&
        (shift.canvassers || []).some(
          (sc) => +sc.canvasser === +canvasser_id
        )
    );

    const updateState = (shiftCanvData) => {
      let newCanvList = [...this.state.displayedCanvassers];
      newCanvList.forEach((d) => {
        if (d.id === +shiftCanvData.canvasser) {
          d.assigned = !isNull(shiftCanvData.doorcrew) || d.assigned;
          if (!shiftCanvData.doorcrew) {
            // Recompute assigned from remaining day crews.
            const stillAssigned = this.state.crews.some((crew) =>
              (crew.seats || []).some(
                (s) => +s.canvasser === +shiftCanvData.canvasser
              )
            );
            d.assigned = stillAssigned;
          }
          d.shift = d.shift || +shiftCanvData.shift;
        }
      });
      this.setState({
        displayedCanvassers: newCanvList.map(this.setShiftCanvasserStatus),
      });
    };

    if (existsOnShift) {
      updateShiftCanvasser({
        canvasser: canvasser_id,
        shift: shift_id,
        doorcrew: crew_id,
      }).then(updateState);
    } else {
      // create a shift canvasser so other sitecanvasses can see this person was scheduled.
      createShiftCanvasser({
        canvasser: canvasser_id,
        shift: shift_id,
        doorcrew: crew_id,
        scheduled: false,
        present: false,
      }).then(updateState);
    }
  }

  handleTurfNoteCreateOrDestroy = (turf) => {
    let turfs = [...this.state.turfs];
    let crews = [...this.state.crews];
    let turf_idx = turfs.findIndex((d) => d.id === turf.id);
    let seat_idx;
    let crew_idx = crews.findIndex((d) => {
      seat_idx = d.seats.findIndex((e) => e.turf.id === turf.id);
      if (seat_idx > -1) {
        return true;
      } else {
        return false;
      }
    });
    if (turf_idx > -1) {
      this.props.setTurfNoteModalTurf(turf);
      turfs[turf_idx] = turf;
    }

    if (crew_idx > -1 && seat_idx > -1) {
      crews[crew_idx].seats[seat_idx].turf = turf;
    }
    this.setState({ turfs, crews });
  };

  // note, to create a crewcanvasser, you can leave that argument null
  _handleCanvasserSelect = (crewcanvasser_id, canvasser_id) => {
    //TODO: HERE, AFTER CANVASSER HAS BEEN UPDATED, DISPATCH SORTING AND DROPOFF ASSIGNMENT TASK TO BACKEND.
    //this backend would take the crew id, assign the dropoff point using straight line geometry, optimize points then set the dropofforder and the dropoff point in the table.
    crewcanvasser_id = +crewcanvasser_id;
    canvasser_id = canvasser_id !== null ? +canvasser_id : null;

    let { crews } = this.state;
    let crew_idx = crews.findIndex((d) =>
      d.seats.map((d) => d.id).includes(crewcanvasser_id)
    );
    let seat_prev = cloneDeep(
      crews[crew_idx].seats.find((d) => d.id === crewcanvasser_id)
    );
    this.setState({ loading: true });

    return doorCalls
      .updateDoorCrewCanvasser(crewcanvasser_id, {
        canvasser: canvasser_id,
      })
      .then((data) => {
        let seats = data.seats.map((d) => ({ ...d, crewcanvasser_id: d.id }));
        let seat = seats.find((d) => d.crewcanvasser_id === crewcanvasser_id);
        crews[crew_idx] = this.addTurfsToSeats(data);
        seat.already_assigned_to = seat_prev.canvasser;
        this.setState({ crews, loading: false });
        return seat;
      })
      .then((data) => {
        const crewShift = crews[crew_idx]?.shift;
        // remove the person if they were there already
        if (data.already_assigned_to) {
          this.updateOrCreateShiftCanvasser(
            data.already_assigned_to,
            null,
            crewShift
          );
        }
        // update the canvasser so they are marked as assigned in thelist
        this.updateOrCreateShiftCanvasser(data.canvasser, data.crew, crewShift);
      });
  };

  _handleDeleteCrew = (id) => {
    this.setState({ loading: true });
    // let crew_canvasser_id = this.state.crews.find(d => d.id === id).seats.map(d => d.id)

    let { turf_ids, crew_canvasser_ids } = this.state.crews.reduce(
      (r, d) => {
        if (d.id !== id) return r;
        d.seats.forEach((s) => {
          r.crew_canvasser_ids.push(s.canvasser);
          r.turf_ids.push(s.turf.id);
        });
        return r;
      },
      { turf_ids: [], crew_canvasser_ids: [] }
    );

    doorCalls.deleteDoorCrew(id).then(() => {
      //remove assigned flag from assignedSites in crew;
      let newCanvList = [...this.state.displayedCanvassers].map((d) => ({
        ...d,
        assigned: crew_canvasser_ids.includes(d.id) ? false : d.assigned,
      }));
      let newTurfList = this.state.turfs.map((d) => ({
        ...d,
        assigned: turf_ids.includes(d.id) ? false : d.assigned,
      }));

      this.setState({
        crews: this.state.crews.filter((d) => d.id !== id),
        displayedCanvassers: newCanvList,
        turfs: newTurfList,
        loading: false,
      });
    });
  };

  _handleDeleteSeat = (seat_id, turf_id, canvasser_id) => {
    this.setState({ loading: true });
    return doorCalls.deleteDoorCrewCanvasser(seat_id).then((crew) => {
      //door canvass calls return the fully modified crew
      // remove the shift canvasser's reference to the crew so other sitecanvasses know they are not scheduled
      let crews = this.state.crews.map((d) => (d.id === crew.id ? crew : d));
      let turfs = this.state.turfs.map((d) =>
        d.id === turf_id ? { ...d, assigned: 0 } : d
      );
      this.updateOrCreateShiftCanvasser(canvasser_id, null, crew.shift);
      this.setState({ loading: false, crews, turfs });
    });
  };

  _renderSidebarTurfs() {
    const debouncedTurfSearch = debounce((query) => {
      const stateChanges = handleSearchChange(query);
      this.setState(
        {
          ...stateChanges[0],
          turfFilters: {
            ...this.state.canvasserFilters,
            keyword: stateChanges[1],
          },
        },
        () => this._filterTurfs()
      );
    }, 300);

    return (
      <>
        <div className={"canvasser-legend"}>
          <div className="filter-text">
            {(!!this.state.turfFilterText ||
              !!this.state.turfPassFilter ||
              !!this.state.intendedWalkStartDate ||
              !!this.state.turfCodeFilter) && (
                <div>
                  {/*<h6 style={{ marginBottom: 5 }}>Filtering By:</h6>*/}
                  {!!this.state.turfFilterText && (
                    <div>Name: {this.state.turfFilterText}</div>
                  )}
                  {!!this.state.turfPassFilter && (
                    <div>
                      Pass:{" "}
                      {
                        (
                          this.props.doorCanvassPasses.find(
                            (d) => this.state.turfPassFilter === d.id
                          ) || {}
                        ).name
                      }
                    </div>
                  )}
                  {(this.state.intendedWalkStartDate ||
                    this.state.intendedWalkEndDate) && (
                      <div>
                        Dates:
                        {Moment(this.state.intendedWalkStartDate).format(
                          "M/D"
                        )} - {Moment(this.state.intendedWalkEndDate).format("M/D")}
                      </div>
                    )}
                  <div>
                    {!isNaN(
                      (
                        this.props.turfCodes.find(
                          (d) => this.state.turfCodeFilter === d.id
                        ) || {}
                      ).name
                    )
                      ? startCase(
                        (
                          this.props.turfCodes.find(
                            (d) => this.state.turfCodeFilter === d.id
                          ) || {}
                        ).name
                      )
                      : startCase(this.state.turfCodeFilter)}{" "}
                    Turfs
                  </div>
                </div>
              )}

            <FilterDisplay
              openFilterModal={() => this.setState({ showTurfFilters: true })}
              filters={this.state.turfFilters}
              handleSearchChange={(val) => {
                this.setState({
                  turfFilters: {
                    ...this.state.turfFilters,
                    keywordPersist: "",
                  },
                });
                debouncedTurfSearch(val);
              }}
              handleSortUpdate={(val) => {
                this.setState(
                  {
                    turfFilters: {
                      ...this.state.turfFilters,
                      sortBy: val,
                    },
                  },
                  this._filterTurfs
                );
              }}
              handleSortDirectionChange={() =>
                this.setState({
                  turfFilters: {
                    ...this.state.turfFilters,
                    sortDsc: !this.state.turfFilters.sortDsc,
                  },
                })
              }
            />
          </div>
        </div>
        <Droppable
          droppableId="turfs"
          type="TURF"
          style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
            >
              {this._filterTurfs().map((item, index) => {
                return (
                  <DraggableTurfAssignment
                    key={"turf-" + item.id}
                    index={index}
                    id={`turf-card-${item.id}`}
                    name={item.name}
                    draggableId={`turf-card_${item.id}`}
                    dragType="TURF"
                    color={this._getSidebarTurfColor(item)}
                    availableRoute={true}
                    data={item}
                    handleMouseUp={() => {
                      this.setCrewDropMatsActive(false);
                      this.makeAddTurfDropVisible(false);
                    }}
                    beforeDrag={() => {
                      this.setCrewDropMatsActive(true, {
                        inline:
                          this.isShowingAllShifts() ||
                          this.getVisibleCrews().length > 0,
                      });
                      this.makeAddTurfDropVisible(true);
                    }}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  }

  _getSidebarTurfColor = (turf) => {
    if (turf.assigned) {
      return this.turfClassMapping["assigned"].color;
    } else {
      return +_get(turf, "agg_data.targets_attempted", 0)
        ? this.turfClassMapping["walked"].color
        : this.turfClassMapping["unwalked"].color;
    }
  };

  _setTurfStatusFilterVisible = (filter_name, visible) => {
    this.setState((prev) => {
      const hasFilter = prev.turfStatusFilters.includes(filter_name);
      if (visible && hasFilter) {
        return {
          turfStatusFilters: prev.turfStatusFilters.filter(
            (d) => d !== filter_name
          ),
        };
      }
      if (!visible && !hasFilter) {
        return {
          turfStatusFilters: [...prev.turfStatusFilters, filter_name],
        };
      }
      return null;
    });
  };

  _renderTurfFilterOptions() {
    let filters = (
      <div className={"turf-filters"}>
        <Input
          type={"select"}
          value={this.state.turfPassFilter}
          title="Pass"
          onChange={(val) => {
            this.setState({ turfPassFilter: +val });
          }}
          options={[
            { value: 0, display: "All Passes" },
            ...this.props.doorCanvassPasses
              .filter((d) => d.canvass?.id === +this.focusedDoorCanvass)
              .map((d) => ({ value: d.id, display: d.name })),
          ]}
        />
        <div className={"label-imitation"}>Walk Status</div>
        <div className={"flx space_between"}>
          {[
            { key: "assigned", label: "Assigned" },
            { key: "unwalked", label: "Not Walked" },
            { key: "walked", label: "Walked" },
          ].map(({ key, label }) => {
            const color = this.turfClassMapping[key].color;
            return (
              <FormControlLabel
                key={key}
                label={label}
                sx={{
                  transform: "scale(0.85)",
                  transformOrigin: "left center",
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "12px",
                    fontWeight: 600,
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={!this.state.turfStatusFilters.includes(key)}
                    onChange={(e) =>
                      this._setTurfStatusFilterVisible(key, e.target.checked)
                    }
                    sx={{
                      color,
                      "&.Mui-checked": { color },
                    }}
                  />
                }
              />
            );
          })}
        </div>

        <Input
          type={"select"}
          value={this.state.turfCodeFilter}
          title="Turf Code"
          onChange={(val) => {
            this.setState({ turfCodeFilter: val });
          }}
          options={[
            { value: "walkable", display: "All Walkable" },
            { value: "not_walkable", display: "All Not Walkable" },
            { value: "all", display: "All" },
            ...this.props.turfCodes
              // .filter((d) => d.walkable)
              .map((d) => ({ value: d.id, display: d.name })),
          ]}
        />

        <div className={"label-imitation"}>Intended Walk Date</div>
        <div className={"flx"}>
          <FromToDatePickerInput
            handleDateChange={(from, to) => {
              this.setState({
                intendedWalkStartDate: from,
                intendedWalkEndDate: to,
              });
            }}
            from={this.state.intendedWalkStartDate}
            to={this.state.intendedWalkEndDate}
            minDate={new Date(2020, 0, 1)}
            maxDate={new Date(2100, 0, 1)}
          />
          <button
            className={"button button-clear"}
            onClick={() => {
              this.setState({
                intendedWalkStartDate: null,
                intendedWalkEndDate: null,
              });
            }}
          >
            Clear Selected Dates
          </button>
        </div>
        <div className={"label-imitation mt-2"}>Note Tags</div>

        <NoteTagFilter
          onSubmit={(res) => {
            this.setState({
              turfFilters: {
                ...this.state.turfFilters,
                noteTags: [...this.state.turfFilters.noteTags, res],
              },
            });
          }}
          onSelect={() => null}
          categories={this.props.categories.filter((d) => d.for_doors)}
        />

        <NoteTagList
          notes={this.state.turfFilters.noteTags}
          editable
          onRemoveNote={(data) => {
            this.setState({
              turfFilters: {
                ...this.state.turfFilters,
                noteTags: [
                  ...this.state.turfFilters.noteTags.filter(
                    (d) =>
                      !(d.category === data.category && d.note === data.note)
                  ),
                ],
              },
            });
          }}
        />
      </div>
    );

    return filters;
  }

  handleTurfTextFilter = (val) => {
    this.setState({
      turfFilterText: val,
    });
  };

  _filterTurfs = () => {
    let {
      turfs,
      turfStatusFilters,
      turfPassFilter,
      turfCodeFilter,
      intendedWalkStartDate,
      intendedWalkEndDate,
    } = this.state;

    if (this.state.turfFilters.keyword.length >= 2) {
      turfs = turfs.filter((d) =>
        d.name
          .toLowerCase()
          .includes(this.state.turfFilters.keyword.toLowerCase())
      );
    }

    turfStatusFilters.forEach((d) => {
      switch (d) {
        case "walked":
          turfs = turfs.filter(
            (t) => +_get(t, "agg_data.targets_attempted", 0) === 0
          );
          break;

        case "unwalked":
          turfs = turfs.filter(
            (t) => +_get(t, "agg_data.targets_attempted", 0) > 0
          );
          break;

        case "assigned":
          turfs = turfs.filter((t) => !t.assigned);

        default:
          return;
      }
    });

    if (turfPassFilter) {
      turfs = turfs.filter((d) => d.doorcanvasspass === turfPassFilter);
    }

    if (turfCodeFilter) {
      if (turfCodeFilter === "walkable") {
        turfs = turfs.filter((d) => {
          const matching_code =
            this.props.turfCodes.find((tc) => tc.id === d.code) || {};
          return !!matching_code.walkable;
        });
      } else if (turfCodeFilter === "not_walkable") {
        turfs = turfs.filter((d) => {
          const matching_code =
            this.props.turfCodes.find((tc) => tc.id === d.code) || {};
          return !matching_code.walkable;
        });
      } else if (!isNaN(+turfCodeFilter)) {
        turfs = turfs.filter((d) => d.code === turfCodeFilter);
      }
    }

    if (intendedWalkStartDate) {
      turfs = turfs.filter(
        (d) =>
          d.intended_walk_date &&
          Moment(d.intended_walk_date) >= Moment(intendedWalkStartDate)
      );
    }

    if (intendedWalkEndDate) {
      turfs = turfs.filter(
        (d) =>
          d.intended_walk_date &&
          Moment(d.intended_walk_date) <= Moment(intendedWalkEndDate)
      );
    }

    if (this.state.turfFilters.noteTags.length) {
      this.state.turfFilters.noteTags.forEach((noteTagFilter) => {
        turfs = filterItemsByNoteTag(turfs, noteTagFilter);
      });
    }

    return sortItems(
      turfs,
      this.state.turfFilters.sortBy,
      this.state.turfFilters.sortDsc
    );
  };

  _renderTurfFilterModal = () => {
    return (
      <BaseModal
        open={this.state.showTurfFilters}
        onClose={() => this.setState({ showTurfFilters: false })}
        center
      >
        <div className="build-crew-sheet-modal-wrapper">
          <div className={"modal-section"}>
            <h3>Filter Turfs</h3>
            {this._renderTurfFilterOptions()}
          </div>
          <div className={"modal-section"}>
            <div
              className={"button"}
              onClick={() => this.setState({ showTurfFilters: false })}
            >
              OK
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  _renderScheduledCanvassers() {
    // start by getting an array of assigned assignedSites so that we can mark them
    let assigned_canvassers = this.getVisibleAssignedCanvasserIds();

    let canvasserValue =
      this.state.canvasserFilters.keywordPersist !== ""
        ? { value: this.state.canvasserFilters.keyword }
        : { test: 1 };

    const debouncedCanvasserSearch = debounce((query) => {
      const stateChanges = handleSearchChange(query);
      this.setState(
        {
          ...stateChanges[0],
          canvasserFilters: {
            ...this.state.canvasserFilters,
            keyword: stateChanges[1],
          },
        },
        () =>
          filterAndSortItems(this.props.canvassers, this.state.canvasserFilters)
      );
    }, 300);

    return (
      <>
        <CanvasserLegend
          open={this.state.showLegendModal}
          handleFilterUpdate={(statuses, assigned) => {
            this.setState({
              showLegendModal: false,
              displayedCanvasserStatuses: statuses,
              showAssignedCanvassers: assigned,
            });
          }}
        />
        <FilterDisplay
          filters={this.state.canvasserFilters}
          openFilterModal={() => this.setState({ showLegendModal: true })}
          handleSearchChange={(val) => {
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                keywordPersist: "",
              },
            });
            debouncedCanvasserSearch(val);
          }}
          handleSortUpdate={(val) => {
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                sortBy: val,
              },
            });
          }}
          handleSortDirectionChange={() =>
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                sortDsc: !this.state.canvasserFilters.sortDsc,
              },
            })
          }
          value={canvasserValue}
        />

        <Droppable
          droppableId="displayedCanvassers"
          type="CANVASSER"
          style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
            >
              {filterAndSortItems(
                this.state.displayedCanvassers,
                this.state.canvasserFilters
              )
                .filter((d) =>
                  this.state.displayedCanvasserStatuses.includes(d.status)
                )
                .filter((d) =>
                  this.state.showAssignedCanvassers
                    ? true
                    : !assigned_canvassers.includes(d.id)
                )
                .map((item, index) => (
                  <DraggableShiftCanvasser
                    key={item.lastname + "-" + item.id}
                    index={index}
                    id={`canvasser-card-${item.id}`}
                    name={item.firstname + " " + item.lastname}
                    canvasser={item}
                    draggableId={`canvasser_${item.id}`}
                    dragType="CANVASSER"
                    color={this.colorClassMapping[item.status].color}
                    assigned={assigned_canvassers.includes(item.id)}
                  />
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  }

  _renderCrewDropMat = (droppableId, crewsForShift, snapshot, provided) => {
    return (
      <div
        ref={provided.innerRef}
        id={droppableId === "crewsDrop" ? "crew-drop-mat" : undefined}
        className={`crew-drop-mat${snapshot.isDraggingOver ? " primary-milligram light" : ""}`}
      >
        <div className={`drop-text-wrapper`}>
          {!crewsForShift.length && <div className="drop-text-plus">+</div>}
          <div className="drop-text-sub">
            {!!crewsForShift.length && "+ "}Create a new Crew
          </div>
        </div>
        {provided.placeholder}
      </div>
    );
  };

  _renderCrewCard = (item) => (
    <DoorCrewCard
      key={"crew-" + item.id}
      crew={item}
      shiftData={this.state.shiftData}
      canvassers={this.props.canvassers}
      turfCodes={this.props.turfCodes}
      office={this.state.focusedOffice}
      onDeleteCrew={() => this._handleDeleteCrew(item.id)}
      onCanvasserUpdate={this.setCrewsFromProps}
      onDropoffRequest={() =>
        this.props.navigate(
          `/doorcanvass/${item.canvass}/dropoffs/${item.shift}?crew=${item.id}`
        )
      }
      onOpenTurfNoteModal={this.props.setTurfNoteModalTurf}
    />
  );

  _renderCrews = () => {
    let hasShifts = this.state.shiftData.length > 0;
    let dropMatMessage = hasShifts
      ? "Drag a turf over to start scheduling"
      : "Create a shift before adding routes.";

    const showAll = this.isShowingAllShifts();
    const crews = this.getVisibleCrews();

    if (!hasShifts) {
      return (
        <>
          <Droppable droppableId="crewsDrop" type="TURF">
            {(provided) => (
              <div
                ref={provided.innerRef}
                id="crew-drop-mat"
                className={`crew-drop-mat`}
              >
                <div className={`drop-text-wrapper warning`}>
                  <div className="drop-text-plus">x</div>
                  <div className="drop-text-sub">Create a shift first</div>
                </div>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <div className={"crewlist"}>
            <div className={`empty-drop-mat`}>
              {!this._isLoading() && dropMatMessage}
            </div>
          </div>
        </>
      );
    }

    if (showAll) {
      return (
        <div className={"crewlist"}>
          {this.state.shiftData.map((shift) => {
            const shiftCrews = crews.filter((crew) => crew.shift === shift.id);
            return (
              <div key={`shift-section-${shift.id}`} className="shift-crew-section">
                <div className="shift-crew-section-header primary-text">
                  {this.getShiftLabel(shift)}
                  <span className="shift-crew-section-count">
                    {shiftCrews.length} crew{shiftCrews.length === 1 ? "" : "s"}
                  </span>
                </div>
                <Droppable droppableId={`crewsDrop_${shift.id}`} type="TURF">
                  {(provided, snapshot) =>
                    this._renderCrewDropMat(
                      `crewsDrop_${shift.id}`,
                      shiftCrews,
                      snapshot,
                      provided
                    )
                  }
                </Droppable>
                {shiftCrews.length
                  ? shiftCrews.map((item) => this._renderCrewCard(item))
                  : null}
              </div>
            );
          })}
          {!crews.length && !this._isLoading() && (
            <div className={`empty-drop-mat`}>{dropMatMessage}</div>
          )}
        </div>
      );
    }

    return (
      <>
        <Droppable droppableId="crewsDrop" type="TURF">
          {(provided, snapshot) =>
            this._renderCrewDropMat("crewsDrop", crews, snapshot, provided)
          }
        </Droppable>
        <div className={"crewlist"}>
          {crews.length ? (
            crews.map((item) => this._renderCrewCard(item))
          ) : (
            <div className={`empty-drop-mat`}>
              {!this._isLoading() && dropMatMessage}
            </div>
          )}
        </div>
      </>
    );
  };

  _handleCrewCanvasserChange = (crew_id, seat_data) => {
    let crews = cloneDeep(this.state.crews);
    let crew_index = crews.findIndex((d) => d.crew_id === +crew_id);
    if (crew_index < 0) {
      console.warn(
        "Tried to update a crew, but none found that matches id ",
        crew_id
      );
      return;
    }

    let seat_index = crews[crew_index].routeseats.findIndex(
      (d) => d.crewcanvasser_id === +seat_data.crewcanvasser_id
    );
    if (seat_index > -1) {
      crews[crew_index].routeseats[seat_index] = seat_data;
      this.setState({ crews });
    } else {
      console.warn(
        "Tried to update a crewcanvasser, but none found that matches id ",
        seat_data.crewcanvasser_id
      );
    }
  };

  createNewShift() {
    this.setState({ lockShiftSubmit: true });

    const data = {
      office: this.state.focusedOffice,
      start: this.state.shiftStart,
      end: this.state.shiftEnd,
      date: this.state.selectedDate,
    };

    let timeResult = this.buildStrictTimeString(data);

    if (!timeResult.strictStart || !timeResult.strictEnd) {
      this.setState({ invalidInput: true, lockShiftSubmit: false });
      return;
    }

    if (!this.checkValidRange(timeResult.strictStart, timeResult.strictEnd)) {
      this.setState({ invalidTime: true, lockShiftSubmit: false });
      return;
    }

    data.start = timeResult.strictStart;
    data.end = timeResult.strictEnd;

    createShift(data).then((data) => {
      if (data) {
        let shifts = [...this.state.shiftData, data];
        this.props.setCrewsShiftData(data);

        this.setState({
          shiftData: shifts,
          shiftSuccess: true,
          showShiftModal: false,
          lockShiftSubmit: false,
          crewsheet_selected_shift_idx: shifts.length - 1,
        });

        setTimeout(() => this.setState({ shiftSuccess: false }), 6000);
      } else {
        this.setState({
          shiftError: true,
          showShiftModal: false,
          lockShiftSubmit: false,
        });

        setTimeout(() => this.setState({ shiftError: false }), 6000);
      }
    });
  }

  buildStrictTimeString(data) {
    const convertToAmPm = (hhmm) => {
      if (!hhmm || !hhmm.includes(":")) return null;
      let [h, m] = hhmm.split(":").map(Number);

      const ampm = h >= 12 ? "pm" : "am";
      let hour12 = h % 12;
      if (hour12 === 0) hour12 = 12;

      return `${hour12}:${m.toString().padStart(2, "0")}${ampm}`;
    };

    const strictStart = convertToAmPm(data.start);
    const strictEnd = convertToAmPm(data.end);

    return { strictStart, strictEnd };
  }

  checkValidRange(start, end) {
    return Moment(start, "h:mma").isBefore(Moment(end, "h:mma"));
  }

  setOrUnsetDropMapActive = (elem_id, set = true) => {
    document.getElementById(elem_id).className = "crew-drop-mat active";
  };

  _validateDateTime(data) {
    let valid = false;
    let validRange = false;
    let strictStart;
    let strictEnd;

    const cleanInput = (str) => str.replace(/\s/g, "").toLowerCase();

    if (!data.start || !data.end) {
      this.setState({ invalidInput: !valid });
      return valid;
    }

    const _start = cleanInput(data.start);
    const _end = cleanInput(data.end);

    valid = Moment(_start, "LT", true).isValid();
    valid = Moment(_end, "LT", true).isValid();
    validRange = Moment(_start, "LT").isBefore(Moment(data.end, "LT"));

    if (!valid) {
      this.setState({ invalidInput: !valid });
    } else if (!validRange) {
      // start was after end, not allowed.
      this.setState({ invalidTime: !validRange });
      valid = false;
    } else {
      // moment is lax with time values, so only capture and set the exact digits we want if it's valid.
      // ie: 009:1811am is a valid time to moment despite the LT format above. Don't submit that shift
      // This regex has 3 capture groups to get each sensical piece of time to reconstruct w/o the extra things moment allows.
      let startPieces = _start.match(
        /^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/
      );
      let endPieces = _end.match(
        /^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/
      );

      if (startPieces && endPieces) {
        strictStart = startPieces[1] + ":" + startPieces[2] + startPieces[3];
        strictEnd = endPieces[1] + ":" + endPieces[2] + endPieces[3];
      } else {
        // in the event the user has done something really wild and it got past moment, but failed the regex
        // send it back the user to fix.
        this.setState({ invalidInput: true });
        valid = false;
      }
    }

    return { valid, strictStart, strictEnd };
  }

  _handleTimeChange(key, val) {
    this.setState({
      [key]: val.trim(),
      invalidInput: false,
      invalidTime: false,
    });
  }

  _renderCreateShiftModal() {
    return (
      <BaseModal
        open={this.state.showShiftModal}
        onClose={() => this.setState({ showShiftModal: false })}
        center
        styles={{
          modal: { minWidth: "420px", width: "auto" },
        }}
      >
        <div className="container">
          <i
            className="fas fa-pull-right fa-times"
            style={{ cursor: "pointer" }}
            onClick={() => this.setState({ showShiftModal: false })}
          />
          <h4 className={"primary-text"}>
            Create A Shift{" "}
            <span style={{ fontSize: ".7em" }}>
              ({this.state.selectedDate})
            </span>
          </h4>

          <span className={"secondary-text"}>Start Time</span>
          <Input
            type="time"
            step="900"
            onChange={this._handleTimeChange.bind(this, "shiftStart")}
            value={this.state.shiftStart || ""}
          />

          <span className={"secondary-text"}>End Time</span>
          <Input
            type="time"
            step="900"
            onChange={this._handleTimeChange.bind(this, "shiftEnd")}
            value={this.state.shiftEnd || ""}
          />

          {this.state.invalidInput && (
            <div className="danger-text" style={{ fontWeight: "bold" }}>
              Invalid Date / Time
            </div>
          )}
          {this.state.invalidTime && (
            <div className="danger-text" style={{ fontWeight: "bold" }}>
              End must be after start time
            </div>
          )}

          <button
            disabled={
              !this.state.shiftStart ||
              !this.state.shiftEnd ||
              this.state.lockShiftSubmit
            }
            className="button button-outline text-primary"
            onClick={this.createNewShift.bind(this)}
          >
            Submit
          </button>
        </div>
      </BaseModal>
    );
  }

  _changeDate = (add = true) => {
    this.setState({ crews: [] });

    let { selectedDate } = this.state;
    let mom = new Moment(selectedDate, "YYYY-MM-DD");

    if (add) {
      mom = mom.add(1, "days");
    } else {
      mom = mom.subtract(1, "days");
    }

    let formatted_mom = mom.format("YYYY-MM-DD");
    this.setState(
      { selectedDate: formatted_mom, crewsheet_selected_shift_idx: 0 },
      this._setDateChangeTimeout
    );
  };

  _setDateChangeTimeout() {
    clearTimeout(this.dateChangeTimeout);
    this.dateChangeTimeout = setTimeout(() => this.getDataOnLoad(), 750);
  }

  _isLoading = () => {
    return (
      (this.props.siteroutes_loading ||
        this.props.canvassers_loading ||
        this.state.loading) &&
      !this._isError()
    );
  };

  _isError = () => {
    return (
      this.props.canvassers_error ||
      this.props.siteroutes_error ||
      this.state.shiftError ||
      this.state.pageError
    );
  };

  //todo this is broken
  _addOrRemoveCrewFromCrewSheet = (crew_id) => {
    const inSheet = this.state.crewsheet_crews.includes(crew_id);
    if (inSheet) {
      let crewsheet_crews = this.state.crewsheet_crews.filter(
        (d) => d !== +crew_id
      );
      this.setState(
        { crewsheet_crews },
        this.props.setCrewsForRouting.bind(
          this,
          this.state.crews.filter((d) => crewsheet_crews.includes(d.crew_id))
        )
      );
    } else {
      let crewsheet_crews = [...this.state.crewsheet_crews, crew_id];
      this.setState(
        { crewsheet_crews },
        this.props.setCrewsForRouting.bind(
          this,
          this.state.crews.filter((d) => crewsheet_crews.includes(d.crew_id))
        )
      );
    }
  };

  _buildCrewSheetModal = () => {
    const visibleCrews = this.getVisibleCrews();
    this.props.setCrewsForRouting(
      visibleCrews.filter((d) => this.state.crewsheet_crews.includes(d.id))
    );
    return (
      <BaseModal
        open={this.state.crewsheet_modal_show}
        onClose={() => this.setState({ crewsheet_modal_show: false })}
        center
      >
        <div className="build-crew-sheet-modal-wrapper">
          <div className={"modal-section"}>
            <h3>Build Crew Sheet</h3>
          </div>
          <div className={"modal-section"}>
            <Input
              type={"switch-left"}
              checked={!this.props.sessionVars.hideDrivingDirections}
              defaultChecked={true}
              onChange={() => {
                this.props.setSessionVar(
                  "hideDrivingDirections",
                  !this.props.sessionVars.hideDrivingDirections
                );
              }}
              title={"Print Driving Directions"}
            />
          </div>
          <div className={"modal-section"}>
            <div className={"flx"}>
              <h5 className={"primary-text"} style={{ marginBottom: 0 }}>
                Filter Crews
              </h5>
              <div className={"ml-6"}>
                <div
                  className="button button-clear "
                  onClick={() => {
                    this.props.setSessionVar(
                      "excludedCrews",
                      this.props.crews.map((d) => d.id)
                    );
                  }}
                >
                  Deselect All
                </div>
                <div
                  className="button button-clear px-0 ml-3"
                  // style={{ marginLeft: "1em" }}
                  onClick={() => {
                    this.props.setSessionVar("excludedCrews", []);
                  }}
                >
                  Select All
                </div>
              </div>
            </div>
            {visibleCrews.map((d) => (
              <Input
                type={"switch-left"}
                key={d.id}
                checked={!this.props.sessionVars.excludedCrews?.includes(d.id)}
                defaultChecked={true}
                onChange={() => {
                  let { excludedCrews } = this.props.sessionVars;
                  if (this.props.sessionVars.excludedCrews?.includes(d.id)) {
                    excludedCrews = excludedCrews.filter((c) => c !== d.id);
                  } else {
                    excludedCrews = excludedCrews
                      ? [...excludedCrews, d.id]
                      : [d.id];
                  }
                  this.props.setSessionVar("excludedCrews", excludedCrews);
                }}
                title={
                  this.isShowingAllShifts()
                    ? `${d.id} (${this.getShiftLabel(
                        this.state.shiftData.find((s) => s.id === d.shift)
                      )})`
                    : d.id
                }
                style={{ padding: "5px", marginBottom: "5px" }}
                classNames={"no-padding no-margin"}
              />
            ))}
          </div>

          <div className={"modal-section"}>
            <div
              className={`button ${this.props.sessionVars?.excludedCrews?.length ===
                  this.props.crews.filter(
                    (crew) => crew.canvass === +this.focusedDoorCanvass
                  ).length
                  ? "disabled"
                  : ""
                }`}
              onClick={this._handlePrintCrewSheetsRequest}
            >
              Print Crew Sheets
            </div>
            <div
              className={"button danger"}
              style={{ marginLeft: "1em" }}
              onClick={() => this.setState({ crewsheet_modal_show: false })}
            >
              Cancel
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  _handleOfficeChange = (val) => {
    this.setState({ focusedOffice: +val }, () => {
      this.getAndParseSchedule().then(() => {
        this.setState({
          displayedCanvassers: this.refreshCanvasserStatuses(),
        });
      });
    });
  };

  _handleShiftChange(val) {
    if (val === ALL_SHIFTS || val === String(ALL_SHIFTS)) {
      this.props.setCrewsShiftData(this.state.shiftData[0] || {});
      this.setState(
        {
          shiftSuccess: false,
          showShiftModal: false,
          crewsheet_selected_shift_idx: -1,
        },
        () => {
          this.setState({
            displayedCanvassers: this.refreshCanvasserStatuses(),
          });
        }
      );
      return;
    }

    const shiftDataIndex = this.state.shiftData.findIndex((d) => d.id === +val);
    let shiftData = {};
    if (shiftDataIndex > -1) shiftData = this.state.shiftData[shiftDataIndex];
    this.props.setCrewsShiftData(shiftData);
    this.setState(
      {
        shiftSuccess: false,
        showShiftModal: false,
        crewsheet_selected_shift_idx: shiftDataIndex,
      },
      () => {
        this.setState({
          displayedCanvassers: this.refreshCanvasserStatuses(),
        });
      }
    );
  }

  _loadCrewSheetModal = () => {
    this.setState({
      crewsheet_modal_show: true,
      crewsheet_crews: this.state.crews.map((d) => d.crew_id),
    });
  };

  _handlePrintCrewSheetsRequest = () => {
    const visibleCrewIds = new Set(this.getVisibleCrews().map((c) => c.id));
    const crewsByShift = {};

    this.props.crews.forEach((crew) => {
      if (
        +crew.canvass === +this.focusedDoorCanvass &&
        visibleCrewIds.has(crew.id) &&
        !this.props.sessionVars.excludedCrews?.includes(crew.id)
      ) {
        if (!crewsByShift[crew.shift]) crewsByShift[crew.shift] = [];
        crewsByShift[crew.shift].push(crew.id);
      }
    });

    Object.entries(crewsByShift).forEach(([shiftId, crewsArr]) => {
      const searchParams = new URLSearchParams();
      searchParams.append("crews", crewsArr);
      window.open(
        `/door-crewsheet/${shiftId}/?${searchParams.toString()}`,
        "_blank"
      );
    });
  };

  render() {
    const shiftCrews = this.getVisibleCrews();
    const shiftSelectValue = this.isShowingAllShifts()
      ? ALL_SHIFTS
      : this.getSelectedShiftId() || "";

    const canvassers = this.state.displayedCanvassers;
    const canvasserTotal = canvassers.length;
    const checkedInCount = findCheckedInCanvassers(canvassers).length;
    const notCheckedInCount = canvassers.filter(
      (d) => d.status === "not_checked_in"
    ).length;
    // Same partition as status colors: assigned === checked in + not checked in.
    const assignedCount = checkedInCount + notCheckedInCount;
    const unscheduledCount = canvassers.filter(
      (d) => d.status === "unscheduled"
    ).length;
    const isSelectedDateToday = Moment(this.state.selectedDate).isSame(
      Date.now(),
      "day"
    );

    return (
      <NavBaseTemplate disabled={true} >
        <DragDropContext onDragStart={this.onDragStart} onDragEnd={this.onDragEnd}>
          <div className="flx space_between align_base schedule-title-flex">
            <h3 className="primary-text">
              Schedule Door Crews
              {this.state.focusedDoorCanvassName
                ? `: ${this.state.focusedDoorCanvassName}`
                : ""}
            </h3>
            <div>
              {/* <a href={`/door-crewsheet/${this.props.shift_data.id}/`} target="_blank"> */}
              <button
                disabled={
                  !shiftCrews.length ||
                  this.state.loading ||
                  !this.state.shiftData.length
                }
                className={`button button-outline text-primary ${
                  shiftCrews.length &&
                  !this.state.loading &&
                  this.state.shiftData.length
                    ? ""
                    : "disabled"
                }`}
                onClick={this._loadCrewSheetModal}
              >
                Build Crew Sheets
              </button>
              {/* </a> */}
              <button
                style={{
                  marginLeft: "1em",
                  color: "#2F96B4",
                  borderColor: "#2F96B4",
                }}
                className={`button button-outline text-primary`}
                onClick={() => this.setState({ showShiftModal: true })}
              >
                Create A Shift
              </button>
            </div>
          </div>

          <div className="row align-items-flex-start" style={{ alignItems: 'flex-start' }}>
            <div className="column column-80">
              <div className="flx space_between schedule-options-flex">
                <div className="flx justify-start date-select-line">
                  <i
                    className={`fas fa-arrow-left fa-pull-left
                                                    ${new Moment(
                      Date.now()
                    ).format("YYYY-MM-DD") ===
                        this.state.selectedDate
                        ? ""
                        : null
                      }
                                                `}
                    onClick={() => this._changeDate(false)}
                    title="Previous Day"
                  />
                  <h5>{this.state.selectedDate}</h5>
                  <i
                    className="fas fa-arrow-right fa-pull-right"
                    title="Next Day"
                    onClick={() => this._changeDate(true)}
                  />
                </div>
                <div className="flx justify-end">
                  <div className="override-milligram scheduler-drop-down">
                    {this.state.shiftData.length > 0 ? (
                      <>
                        <div className="mr-3">Shift</div>
                        <Input
                          type={"select"}
                          style={{ minWidth: "180px" }}
                          placeholder={"Select a shift"}
                          value={shiftSelectValue}
                          options={[
                            ...this.state.shiftData.map((d) => ({
                              value: d.id,
                              display: this.getShiftLabel(d),
                            })),
                            { value: ALL_SHIFTS, display: "All shifts" },
                          ]}
                          onChange={(val) => this._handleShiftChange(val)}
                        />
                      </>
                    ) : null}
                  </div>

                  <div className="override-milligram scheduler-drop-down">
                    <div className="mr-3">Office</div>
                    <Input
                      type={"select"}
                      value={this.state.focusedOffice}
                      options={
                        this.props.offices
                          ? this.props.offices.map((d, i) => ({
                            value: d.id,
                            display: d.name,
                          }))
                          : null
                      }
                      onChange={(val) => this._handleOfficeChange(val)}
                    />
                  </div>
                </div>
              </div>
              <div className="card wox-card crews-card">
                <div className="">{this._renderCrews()}</div>
              </div>
            </div>
            <div className="column column-20">
              <Tabs
                tabs={[
                  {
                    display: (
                      <span className="tab-label">
                        <i style={{ padding: '0 0.5em 0 1em' }} className="fas fa-route" />
                        Turfs
                      </span>
                    ),
                    onClick: () => this.setState({ sideBarSelection: "turfs" }),
                    active: this.state.sideBarSelection === "turfs",
                  },
                  {
                    display: (
                      <span className="tab-label">
                        <i style={{ padding: '0 0.5em 0 1em' }} className="fas fa-user-clock" />
                        Canvassers
                      </span>
                    ),
                    onClick: () =>
                      this.setState({ sideBarSelection: "canvassers" }),
                    active: this.state.sideBarSelection === "canvassers",
                  },
                ]}
                style={{
                  marginBottom: ".8em",
                  justifyContent: "center",
                }}
              />

              <div className="card wox-card crews-card assigned-canvasser-counter">
                <div className="facet-count-row facet-count-total">
                  <span className="facet-count-num">{canvasserTotal}</span>
                  canvassers
                </div>
                {isSelectedDateToday ? (
                  <>
                    <div
                      className={`facet-count-row ${CANVASSER_STATUS_COLORS.checked_in.class}`}
                    >
                      <span className="facet-count-num">{checkedInCount}</span>
                      checked in
                    </div>
                    <div
                      className={`facet-count-row ${CANVASSER_STATUS_COLORS.not_checked_in.class}`}
                    >
                      <span className="facet-count-num">
                        {notCheckedInCount}
                      </span>
                      not checked in
                    </div>
                    <div
                      className={`facet-count-row ${CANVASSER_STATUS_COLORS.unscheduled.class}`}
                    >
                      <span className="facet-count-num">
                        {unscheduledCount}
                      </span>
                      unscheduled
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`facet-count-row ${CANVASSER_STATUS_COLORS.assigned.class}`}
                    >
                      <span className="facet-count-num">
                        {canvasserTotal - unscheduledCount}
                      </span>
                      scheduled
                    </div>
                    <div
                      className={`facet-count-row ${CANVASSER_STATUS_COLORS.unscheduled.class}`}
                    >
                      <span className="facet-count-num">
                        {unscheduledCount}
                      </span>
                      unscheduled
                    </div>
                  </>
                )}
              </div>

              <div className="card wox-card crews-card">
                <div className="sidebar-wrapper">
                  {this.state.sideBarSelection == "turfs"
                    ? this._renderSidebarTurfs()
                    : this._renderScheduledCanvassers()}
                </div>
              </div>
            </div>
          </div>
        </DragDropContext>

        {this.state.crewsheet_modal_show && this._buildCrewSheetModal()}
        {this._renderCreateShiftModal()}
        {this._renderTurfFilterModal()}

        <FeedbackTile
          text={
            <div>
              <i
                className=" fas fa-asterisk fa-spin"
                style={{ marginRight: "15px" }}
              />
              Loading...
            </div>
          }
          visible={this._isLoading()}
        />
        <FeedbackTile
          text={
            <div>
              <i className="fas fa-check" style={{ marginRight: "15px" }}></i>
              Shift Created
            </div>
          }
          visible={this.state.shiftSuccess}
        />

        {/*use feedback tile icon, text, state*/}
        <FeedbackTile
          text={
            <div>
              <i
                className="fas fa-exclamation-triangle"
                style={{ marginRight: "15px" }}
              />
              {this.state.errMsg}
            </div>
          }
          visible={this._isError()}
          classNames="danger"
        />

        <TurfNoteModal
          handleNoteCreateOrDestroy={this.handleTurfNoteCreateOrDestroy}
          showCloseIcon
          center
        />
      </NavBaseTemplate>
    );
  }
} //end class

const mapStateToProps = (state) => ({
  focusedCampaign: state.session.focusedCampaign,
  doorCanvasses: state.doorCanvasses.data,
  doorCanvassPasses: state.doorCanvassPasses.data,
  crews: state.doorCrews.data,
  crews_loading: state.doorCrews.loading,
  crews_error: state.doorCrews.error,
  canvassers: state.canvassers.data,
  canvassers_loading: state.canvassers.loading,
  canvassers_error: state.canvassers.error,
  is_admin: state.permissions.is_admin,
  shift_data: state.crews.shift_data,
  turfCodes: state.turfCodes.data,
  offices: state.offices.data,
  sessionVars: state.session.vars,
  categories: state.siteNotes.categories,
});

const mapDispatchToProps = (dispatch) => ({
  setCrewsForRouting: (crews) => dispatch(setCrews(crews)),
  setCrewsShiftData: (data) => dispatch(setCrewsShiftData(data)),
  getTurfCodes: () => dispatch(doorCalls.getTurfCodes()),
  setTurfNoteModalTurf: (turf) =>
    dispatch(setSessionVar("turfNoteModalTurf", turf)),
  setSessionVar: (key, val) => dispatch(setSessionVar(key, val)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DoorSchedule));
